import React, { useState, useEffect } from 'react';
import { Key, Search, Eye, EyeOff, MessageCircle, Copy, CheckCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ResetPassword() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [userTypeFilter, setUserTypeFilter] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [resetResult, setResetResult] = useState(null);

  useEffect(() => {
    fetchUsers();
  }, [userTypeFilter]);

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      let url = `${API_URL}/api/user-management/users?`;
      if (userTypeFilter) url += `user_type=${userTypeFilter}&`;
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setUsers(data.users || []);
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!selectedUser || !newPassword) {
      toast.error('Please select user and enter new password');
      return;
    }

    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setResetting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/user-management/reset-password`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          user_id: selectedUser.id,
          user_type: selectedUser.user_type,
          new_password: newPassword
        })
      });

      if (response.ok) {
        const result = await response.json();
        toast.success('✅ Password reset successfully!');
        setResetResult({
          username: result.username,
          password: newPassword,
          user_type: result.user_type,
          full_name: selectedUser.full_name,
          phone: selectedUser.phone
        });
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to reset password');
      }
    } catch (error) {
      toast.error('Server error');
    } finally {
      setResetting(false);
    }
  };

  const sendWhatsApp = () => {
    if (!resetResult) return;
    
    const message = encodeURIComponent(
      `🔐 *Password Reset*\n\n` +
      `👤 *Name:* ${resetResult.full_name || 'N/A'}\n` +
      `🆔 *Username:* ${resetResult.username}\n` +
      `🔑 *New Password:* ${resetResult.password}\n\n` +
      `🔗 *Login Link:* ${window.location.origin}/${resetResult.user_type === 'staff' ? 'staff-portal' : 'student-portal'}\n\n` +
      `⚠️ Please change your password after login.`
    );
    
    const phone = resetResult.phone?.replace(/[^0-9]/g, '') || '';
    const whatsappUrl = phone 
      ? `https://wa.me/${phone}?text=${message}`
      : `https://wa.me/?text=${message}`;
    window.open(whatsappUrl, '_blank');
  };

  const filteredUsers = users.filter(user => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      (user.full_name || '').toLowerCase().includes(searchLower) ||
      (user.username || '').toLowerCase().includes(searchLower) ||
      (user.employee_id || '').toLowerCase().includes(searchLower) ||
      (user.student_id || '').toLowerCase().includes(searchLower)
    );
  });

  return (
    <div className="p-6 space-y-6" data-testid="reset-password-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Reset Password</h1>
        <p className="text-gray-500">Reset password for Staff or Student users</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* User Selection */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Search className="h-5 w-5" />
              Select User
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Filters */}
            <div className="flex gap-4">
              <input
                type="text"
                placeholder="Search user..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="flex-1 px-4 py-2 border rounded-lg"
              />
              <select
                value={userTypeFilter}
                onChange={(e) => setUserTypeFilter(e.target.value)}
                className="px-4 py-2 border rounded-lg"
              >
                <option value="">All Types</option>
                <option value="staff">Staff</option>
                <option value="student">Student</option>
              </select>
            </div>

            {/* User List */}
            <div className="max-h-[400px] overflow-y-auto border rounded-lg">
              {loading ? (
                <div className="p-4 text-center text-gray-500">Loading...</div>
              ) : filteredUsers.length === 0 ? (
                <div className="p-4 text-center text-gray-500">No users found</div>
              ) : (
                filteredUsers.map((user) => (
                  <div
                    key={user.id}
                    onClick={() => { setSelectedUser(user); setResetResult(null); setNewPassword(''); }}
                    className={`p-3 border-b cursor-pointer hover:bg-gray-50 ${
                      selectedUser?.id === user.id ? 'bg-blue-50 border-blue-200' : ''
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">{user.full_name || 'N/A'}</p>
                        <p className="text-sm text-gray-500">
                          {user.username || user.employee_id || user.student_id}
                        </p>
                      </div>
                      <span className={`px-2 py-1 rounded text-xs ${
                        user.user_type === 'staff' 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-purple-100 text-purple-700'
                      }`}>
                        {user.user_type === 'staff' ? 'Staff' : 'Student'}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>

        {/* Reset Form */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Key className="h-5 w-5" />
              Reset Password
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {selectedUser ? (
              <>
                <div className="p-4 bg-blue-50 rounded-lg">
                  <p className="font-medium text-blue-800">{selectedUser.full_name || 'N/A'}</p>
                  <p className="text-sm text-blue-600">
                    {selectedUser.user_type === 'staff' ? 'Staff' : 'Student'} • {selectedUser.username}
                  </p>
                </div>

                {!resetResult ? (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        New Password
                      </label>
                      <div className="relative">
                        <input
                          type={showPassword ? "text" : "password"}
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          placeholder="Enter new password (min 6 characters)"
                          className="w-full px-4 py-3 border rounded-lg pr-12"
                          data-testid="new-password-input"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400"
                        >
                          {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                        </button>
                      </div>
                    </div>

                    <Button
                      onClick={handleResetPassword}
                      disabled={resetting || !newPassword}
                      className="w-full bg-blue-600 hover:bg-blue-700"
                      data-testid="reset-btn"
                    >
                      <Key className="h-4 w-4 mr-2" />
                      {resetting ? 'Resetting...' : 'Reset Password'}
                    </Button>
                  </>
                ) : (
                  <div className="space-y-4">
                    <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-center gap-2 text-green-700 mb-3">
                        <CheckCircle className="h-5 w-5" />
                        <span className="font-medium">Password Reset Successful!</span>
                      </div>
                      
                      <div className="space-y-2">
                        <div>
                          <label className="text-xs text-gray-500">Username</label>
                          <div className="flex items-center gap-2">
                            <code className="flex-1 bg-white px-3 py-2 rounded border text-sm">
                              {resetResult.username}
                            </code>
                            <button
                              onClick={() => { navigator.clipboard.writeText(resetResult.username); toast.success('Copied!'); }}
                              className="p-2 hover:bg-gray-100 rounded"
                            >
                              <Copy className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                        <div>
                          <label className="text-xs text-gray-500">New Password</label>
                          <div className="flex items-center gap-2">
                            <code className="flex-1 bg-white px-3 py-2 rounded border text-sm">
                              {showPassword ? resetResult.password : '••••••••'}
                            </code>
                            <button
                              onClick={() => setShowPassword(!showPassword)}
                              className="p-2 hover:bg-gray-100 rounded"
                            >
                              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                            </button>
                            <button
                              onClick={() => { navigator.clipboard.writeText(resetResult.password); toast.success('Copied!'); }}
                              className="p-2 hover:bg-gray-100 rounded"
                            >
                              <Copy className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>

                    <Button
                      onClick={sendWhatsApp}
                      className="w-full bg-green-500 hover:bg-green-600"
                    >
                      <MessageCircle className="h-4 w-4 mr-2" />
                      Share via WhatsApp
                    </Button>

                    <Button
                      onClick={() => { setResetResult(null); setNewPassword(''); }}
                      variant="outline"
                      className="w-full"
                    >
                      Reset Another User
                    </Button>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Key className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                <p>Select a user from the list to reset password</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
